interface BundleStats {
  size: number;
  gzipSize: number;
  timestamp: number;
  chunks: { name: string; size: number }[];
}

interface BundleThresholds {
  warningSize: number; // bytes
  errorSize: number;   // bytes
  maxChunks: number;
}

const DEFAULT_THRESHOLDS: BundleThresholds = {
  warningSize: 2 * 1024 * 1024,  // 2MB
  errorSize: 5 * 1024 * 1024,    // 5MB
  maxChunks: 20
};

class BundleMonitor {
  private thresholds: BundleThresholds;
  private alertCallback?: (level: 'warning' | 'error', message: string) => void;

  constructor(thresholds: Partial<BundleThresholds> = {}) {
    this.thresholds = { ...DEFAULT_THRESHOLDS, ...thresholds };
  }

  setAlertCallback(callback: (level: 'warning' | 'error', message: string) => void) {
    this.alertCallback = callback;
  }

  // Simulate bundle analysis (in real implementation, this would integrate with webpack stats)
  async analyzeBundleSize(): Promise<BundleStats> {
    if (process.env.NODE_ENV !== 'production') {
      // Development mode - estimate based on typical React bundle
      return {
        size: 1.5 * 1024 * 1024, // 1.5MB estimate
        gzipSize: 450 * 1024,     // ~30% compression
        timestamp: Date.now(),
        chunks: [
          { name: 'main', size: 800 * 1024 },
          { name: 'vendors', size: 500 * 1024 },
          { name: 'runtime', size: 50 * 1024 }
        ]
      };
    }

    // Production mode - would integrate with actual bundle analysis
    try {
      // This would typically read from webpack-bundle-analyzer output
      // or integrate with build tools to get real bundle statistics
      const response = await fetch('/bundle-stats.json');
      if (response.ok) {
        const data = await response.json();
        return this.parseBundleStats(data);
      }
    } catch (error) {
      console.warn('Failed to fetch bundle stats:', error);
    }

    // Fallback stats
    return {
      size: 0,
      gzipSize: 0,
      timestamp: Date.now(),
      chunks: []
    };
  }

  private parseBundleStats(rawStats: any): BundleStats {
    // Parse webpack bundle analyzer output format
    const totalSize = rawStats.assets?.reduce((sum: number, asset: any) => sum + asset.size, 0) || 0;
    const gzipSize = Math.floor(totalSize * 0.3); // Estimate gzip compression
    
    const chunks = rawStats.chunks?.map((chunk: any) => ({
      name: chunk.names?.[0] || 'unknown',
      size: chunk.size || 0
    })) || [];

    return {
      size: totalSize,
      gzipSize,
      timestamp: Date.now(),
      chunks
    };
  }

  checkBundleHealth(stats: BundleStats): { level: 'ok' | 'warning' | 'error'; message: string } {
    // Check total bundle size
    if (stats.size > this.thresholds.errorSize) {
      return {
        level: 'error',
        message: `Bundle size (${this.formatSize(stats.size)}) exceeds error threshold (${this.formatSize(this.thresholds.errorSize)})`
      };
    }

    if (stats.size > this.thresholds.warningSize) {
      return {
        level: 'warning',
        message: `Bundle size (${this.formatSize(stats.size)}) exceeds warning threshold (${this.formatSize(this.thresholds.warningSize)})`
      };
    }

    // Check chunk count
    if (stats.chunks.length > this.thresholds.maxChunks) {
      return {
        level: 'warning',
        message: `Too many chunks (${stats.chunks.length}). Consider consolidating or lazy loading.`
      };
    }

    // Check for unusually large individual chunks
    const largeChunks = stats.chunks.filter(chunk => chunk.size > 1024 * 1024); // > 1MB
    if (largeChunks.length > 0) {
      return {
        level: 'warning',
        message: `Large chunks detected: ${largeChunks.map(c => `${c.name} (${this.formatSize(c.size)})`).join(', ')}`
      };
    }

    return {
      level: 'ok',
      message: `Bundle health is good (${this.formatSize(stats.size)} total)`
    };
  }

  private formatSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  }

  async monitorBundle(): Promise<void> {
    try {
      const stats = await this.analyzeBundleSize();
      const health = this.checkBundleHealth(stats);

      if (health.level !== 'ok' && this.alertCallback) {
        this.alertCallback(health.level as 'warning' | 'error', health.message);
      }

      // Store stats for trending analysis
      this.storeBundleStats(stats);
    } catch (error) {
      console.error('Bundle monitoring failed:', error);
      if (this.alertCallback) {
        this.alertCallback('error', 'Bundle monitoring system failed');
      }
    }
  }

  private storeBundleStats(stats: BundleStats): void {
    try {
      const key = 'bundle-stats-history';
      const stored = localStorage.getItem(key);
      const history: BundleStats[] = stored ? JSON.parse(stored) : [];
      
      // Keep last 10 entries
      history.push(stats);
      if (history.length > 10) {
        history.shift();
      }
      
      localStorage.setItem(key, JSON.stringify(history));
    } catch (error) {
      console.warn('Failed to store bundle stats:', error);
    }
  }

  getBundleHistory(): BundleStats[] {
    try {
      const stored = localStorage.getItem('bundle-stats-history');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.warn('Failed to load bundle history:', error);
      return [];
    }
  }

  // Start periodic monitoring
  startPeriodicMonitoring(intervalMs: number = 5 * 60 * 1000): () => void {
    const interval = setInterval(() => {
      this.monitorBundle();
    }, intervalMs);

    // Return cleanup function
    return () => clearInterval(interval);
  }
}

export default BundleMonitor;
export type { BundleStats, BundleThresholds };